import { useEffect, useMemo, useRef, useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import { PaperclipIcon, PencilIcon, PlusIcon, EyeIcon, CircleHelpIcon } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import AppLayout from '@/layouts/AppLayout'
import { PageContainer } from '@/components/PageContainer'
import { PageHeader } from '@/components/PageHeader'
import { FlashBanner } from '@/components/FlashBanner'
import { ErrorBanner } from '@/components/ErrorBanner'
import { StatusBadge } from '@/components/StatusBadge'
import { ScreenGuideDialog } from '@/components/ScreenGuideDialog'
import { Button } from '@/components/ui/button'
import { useScreenGuide } from '@/hooks/useScreenGuide'
import { usePollUntil } from '@/hooks/usePolling'
import { cn } from '@/lib/utils'
import type { ProjectNav } from '@/types/project'
import type { SourceNote } from '@/types/source_note'
import type { ResultError } from '@/types/form'
import type { SharedData } from '@/types/shared'
import { formatDate } from '@/lib/date'
import { onboardingContent } from './onboardingContent'

interface Props {
  project_nav: ProjectNav
  source_notes?: SourceNote[]
  error?: ResultError
}

function hasPendingNote (notes: SourceNote[]): boolean {
  return notes.some(
    (n) =>
      n.status === 'raw' &&
      (n.pipeline_status === 'running' || n.pipeline_status === 'queued')
  )
}

const fieldInputClass =
  'w-full py-[9px] px-3 border border-(--bp-border) rounded-[7px] text-sm text-(--bp-ink) bg-(--bp-surface) outline-none transition-all focus:border-(--bp-accent) focus:shadow-[0_0_0_3px_var(--bp-accent-pale)]'

export default function SourceNotesIndex ({
  project_nav: project,
  source_notes: sourceNotes = [],
  error
}: Props): React.ReactElement {
  const rdBase = `/projects/${project.id}`

  const { current_user: currentUser } = usePage<SharedData>().props
  const guide = useScreenGuide(
    'source_notes',
    currentUser?.onboardings ?? {},
    currentUser?.account_type === 'demo'
  )
  const helpButton = (
    <Button type='button' variant='ghost' size='sm' onClick={() => { guide.openHelp() }}>
      <CircleHelpIcon size={14} /> 使い方
    </Button>
  )
  const guideDialog = (
    <ScreenGuideDialog
      open={guide.open}
      onClose={() => { guide.close() }}
      title={onboardingContent.title}
      sections={onboardingContent.sections}
    />
  )

  // ノート一覧（降順: 最新 → 最古）
  const sortedNotes = useMemo(
    () =>
      [...sourceNotes].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      ),
    [sourceNotes]
  )

  // ラウンド番号用（昇順: 最古 → 最新）
  const notesByAge = useMemo(
    () =>
      [...sourceNotes].sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      ),
    [sourceNotes]
  )

  // 選択ノート
  const [selectedNoteId, setSelectedNoteId] = useState<number | null>(() => {
    const sorted = [...sourceNotes].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )
    return sorted[0]?.id ?? null
  })
  const selectedNote = sortedNotes.find((n) => n.id === selectedNoteId) ?? null

  // 編集状態（structurizing ノート用）
  const [editContent, setEditContent] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'raw' | 'structured'>('structured')
  const [editMode, setEditMode] = useState(false)
  const [savingEdit, setSavingEdit] = useState(false)
  const [analyzingNoteId, setAnalyzingNoteId] = useState<number | null>(null)

  // 初回ノート入力フォーム（状態1用）
  const [inputText, setInputText] = useState('')
  const [initialFile, setInitialFile] = useState<File | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [isDraggingInitial, setIsDraggingInitial] = useState(false)
  const initialFileRef = useRef<HTMLInputElement>(null)

  // 追加ノート モーダル
  const [modalOpen, setModalOpen] = useState(false)
  const [modalPhase, setModalPhase] = useState<'form' | 'processing'>('form')
  const [modalContent, setModalContent] = useState('')
  const [modalFile, setModalFile] = useState<File | null>(null)
  const [modalSubmitting, setModalSubmitting] = useState(false)
  const [isDraggingModal, setIsDraggingModal] = useState(false)
  const modalFileRef = useRef<HTMLInputElement>(null)

  const skipGuardRef = useRef(false)
  const [pollTimedOut, setPollTimedOut] = useState(false)

  const initialNote = sourceNotes.find((n) => n.kind === 'initial')
  const pendingExists = hasPendingNote(sourceNotes)

  const isDirty =
    editContent !== null &&
    editContent !== (selectedNote?.structured_content ?? '')

  const previewContent =
    editContent !== null ? editContent : (selectedNote?.structured_content ?? '')

  // ポーリング（処理中ノートがあるとき）
  usePollUntil(pendingExists, 5000, { maxAttempts: 100, onError: () => setPollTimedOut(true) })

  // ナビゲーションガード（未保存編集あり）
  useEffect(() => {
    if (!isDirty) return
    const handleBeforeUnload = (e: BeforeUnloadEvent): void => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    const removeInertiaGuard = router.on('before', (event) => {
      if (skipGuardRef.current) return
      if (!window.confirm('編集内容が保存されていません。このページを離れてもよろしいですか？')) {
        event.preventDefault()
      }
    })
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      removeInertiaGuard()
    }
  }, [isDirty])

  // 選択ノート変更時: editContent・activeTab 初期化
  useEffect(() => {
    setActiveTab('structured')
    if ((selectedNote == null) || selectedNote.status !== 'structurizing') {
      setEditContent(null)
      setEditMode(false)
      return
    }
    setEditContent(selectedNote.structured_content ?? '')
  }, [selectedNote?.id, selectedNote?.status])

  // モーダル自動クローズ（追加ノートの構造化完了時）
  useEffect(() => {
    if (modalPhase !== 'processing') return
    if (hasPendingNote(sourceNotes)) return
    const newest = [...sourceNotes].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )[0]
    setSelectedNoteId(newest?.id ?? null)
    setModalOpen(false)
    setModalPhase('form')
    setModalContent('')
    setModalFile(null)
  }, [sourceNotes, modalPhase])

  function noteRound (note: SourceNote): number {
    return notesByAge.findIndex((n) => n.id === note.id) + 1
  }

  function applyFile (
    file: File,
    setContent: (v: string) => void,
    setFile: (f: File | null) => void
  ): void {
    setFile(file)
    setContent(`（ファイル選択済み: ${file.name}）`)
  }

  function handleFileSelect (
    e: React.ChangeEvent<HTMLInputElement>,
    setContent: (v: string) => void,
    setFile: (f: File | null) => void
  ): void {
    const file = e.target.files?.[0]
    if (file == null) return
    applyFile(file, setContent, setFile)
    e.target.value = ''
  }

  function makeDragHandlers (
    setDragging: (v: boolean) => void,
    setContent: (v: string) => void,
    setFile: (f: File | null) => void
  ): {
      onDragOver: (e: React.DragEvent) => void
      onDragLeave: (e: React.DragEvent) => void
      onDrop: (e: React.DragEvent) => void
    } {
    return {
      onDragOver: (e: React.DragEvent): void => {
        e.preventDefault()
        setDragging(true)
      },
      onDragLeave: (e: React.DragEvent): void => {
        e.preventDefault()
        setDragging(false)
      },
      onDrop: (e: React.DragEvent): void => {
        e.preventDefault()
        setDragging(false)
        const file = e.dataTransfer.files?.[0]
        if (file != null) applyFile(file, setContent, setFile)
      }
    }
  }

  function handleSubmitInitial (e: React.SubmitEvent): void {
    e.preventDefault()
    if (submitting) return
    if ((initialFile == null) && inputText.trim() === '') return
    setSubmitting(true)
    const data = new FormData()
    if (initialFile != null) {
      data.append('file', initialFile)
    } else {
      data.append('content', inputText)
    }
    router.post(`${rdBase}/source_notes`, data, { onFinish: () => setSubmitting(false) })
  }

  function handleModalSubmit (e: React.SubmitEvent): void {
    e.preventDefault()
    if (modalSubmitting) return
    if ((modalFile == null) && modalContent.trim() === '') return
    setModalSubmitting(true)
    const data = new FormData()
    if (modalFile != null) {
      data.append('file', modalFile)
    } else {
      data.append('content', modalContent)
    }
    router.post(`${rdBase}/source_notes`, data, {
      onSuccess: () => setModalPhase('processing'),
      onFinish: () => setModalSubmitting(false)
    })
  }

  async function handleSaveEdit (note: SourceNote): Promise<boolean> {
    if (savingEdit) return false
    setSavingEdit(true)
    skipGuardRef.current = true
    return await new Promise((resolve) => {
      router.patch(
        `${rdBase}/source_notes/${note.id}`,
        { structured_content: editContent },
        {
          onSuccess: () => resolve(true),
          onError: () => resolve(false),
          onFinish: () => {
            setSavingEdit(false)
            skipGuardRef.current = false
          }
        }
      )
    })
  }

  async function handleAnalyze (note: SourceNote): Promise<void> {
    if (analyzingNoteId !== null) return
    setAnalyzingNoteId(note.id)

    if (isDirty) {
      const saved = await handleSaveEdit(note)
      if (!saved) {
        setAnalyzingNoteId(null)
        return
      }
    }

    // onFinish で skipGuardRef がリセットされた後に await が再開するため、
    // router.post の直前で再セットする
    skipGuardRef.current = true
    router.post(
      `${rdBase}/source_notes/${note.id}/analyze`,
      {},
      {
        onFinish: () => {
          setAnalyzingNoteId(null)
          skipGuardRef.current = false
        }
      }
    )
  }

  function handleSelectNote (noteId: number): void {
    if (selectedNoteId === noteId) return
    if (isDirty && !window.confirm('編集内容が保存されていません。ノートを切り替えてもよろしいですか？')) return
    setSelectedNoteId(noteId)
  }

  // ============================================================
  // 状態1a: 初回ノート失敗（リトライ）
  // ============================================================
  if (initialNote?.status === 'raw' && initialNote.pipeline_status === 'failed') {
    return (
      <AppLayout project={project}>
        <PageContainer maxWidth='760px'>
          <PageHeader
            title='要件ヒアリング'
            subtitle='ヒアリングノートを入力して要件解析を開始します'
          >
            {helpButton}
          </PageHeader>
          {guideDialog}
          <FlashBanner />
          <ErrorBanner message={error?.error_message ?? '構造化中にエラーが発生しました。再度お試しください。'} />
          <div className='bg-(--bp-surface) border border-(--bp-border) rounded-[10px]'>
            <div className='px-6 py-5 border-b border-(--bp-border)'>
              <div className='text-[13px] font-semibold text-(--bp-ink) mb-2'>
                入力済みのヒアリングノート
              </div>
              <pre
                className='text-[12px] text-(--bp-ink2) whitespace-pre-wrap leading-relaxed'
                style={{ fontFamily: 'var(--bp-font-mono)', maxHeight: 200, overflow: 'auto' }}
              >
                {initialNote.content}
              </pre>
            </div>
            <div className='px-6 py-4 bg-(--bp-surface2) border-t border-(--bp-border) rounded-b-[10px] flex gap-2 justify-end items-center'>
              <div className='text-[11px] text-(--bp-ink3) mr-auto'>
                同じ内容で再度構造化を実行します
              </div>
              <Button
                type='button'
                disabled={submitting}
                onClick={() => {
                  if (submitting) return
                  setSubmitting(true)
                  router.post(
                    `${rdBase}/source_notes/${initialNote.id}/retry_structurize`,
                    {},
                    { onFinish: () => setSubmitting(false) }
                  )
                }}
              >
                {submitting ? '送信中...' : '再試行する'}
              </Button>
            </div>
          </div>
        </PageContainer>
      </AppLayout>
    )
  }

  // ============================================================
  // 状態1b: 未開始（入力フォーム）
  // ============================================================
  if (initialNote == null) {
    return (
      <AppLayout project={project}>
        <PageContainer maxWidth='760px'>
          <PageHeader
            title='要件ヒアリング'
            subtitle='ヒアリングノートを入力して要件解析を開始します'
          >
            {helpButton}
          </PageHeader>
          {guideDialog}
          <FlashBanner />
          <form onSubmit={handleSubmitInitial}>
            <div className='bg-(--bp-surface) border border-(--bp-border) rounded-[10px]'>
              <div className='px-6 py-5 border-b border-(--bp-border)'>
                <div className='text-[13px] font-semibold text-(--bp-ink) mb-3.5'>
                  ヒアリングノートを入力
                </div>
                <div
                  className='mb-4.5'
                  style={
                    isDraggingInitial
                      ? { borderColor: 'var(--bp-accent)', background: 'var(--bp-accent-pale)' }
                      : undefined
                  }
                >
                  <textarea
                    className={fieldInputClass}
                    style={{ minHeight: 180, resize: 'vertical' }}
                    placeholder='ミーティングメモ、要求一覧、業務フロー草案などをそのまま貼り付けてください'
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    {...makeDragHandlers(setIsDraggingInitial, setInputText, setInitialFile)}
                  />
                  <div className='text-[11px] text-(--bp-ink3) mt-1'>
                    テキストを直接入力するか、ファイルをドラッグ＆ドロップしてください
                  </div>
                </div>
                <Button
                  type='button'
                  variant='ghost'
                  onClick={() => initialFileRef.current?.click()}
                  style={{ marginTop: 4 }}
                >
                  <PaperclipIcon size={14} /> ファイルから読み込む
                </Button>
                <input
                  ref={initialFileRef}
                  type='file'
                  style={{ display: 'none' }}
                  onChange={(e) => handleFileSelect(e, setInputText, setInitialFile)}
                />
              </div>
              <div className='px-6 py-4 bg-(--bp-surface2) border-t border-(--bp-border) rounded-b-[10px] flex gap-2 justify-end items-center'>
                <div className='text-[11px] text-(--bp-ink3) mr-auto'>
                  入力後、構造化→要件解析が自動で実行されます
                </div>
                <Button type='submit' disabled={submitting}>
                  {submitting ? '送信中...' : '構造化を実行する'}
                </Button>
              </div>
            </div>
          </form>
        </PageContainer>
      </AppLayout>
    )
  }

  // ============================================================
  // 状態2: 構造化中（ポーリング）
  // ============================================================
  if (
    initialNote.status === 'raw' &&
    (initialNote.pipeline_status === 'running' || initialNote.pipeline_status === 'queued')
  ) {
    return (
      <AppLayout project={project}>
        <PageContainer maxWidth='640px'>
          {pollTimedOut && (
            <ErrorBanner message='処理に時間がかかっています。ページを再読み込みしてください。' />
          )}
          <div className='text-center py-15'>
            <div
              style={{
                width: 48,
                height: 48,
                border: '3px solid var(--bp-border)',
                borderTopColor: 'var(--bp-accent)',
                borderRadius: '50%',
                animation: 'spin .8s linear infinite',
                margin: '0 auto 20px'
              }}
            />
            <div className='text-[16px] font-semibold text-(--bp-ink) mb-1.5'>
              構造化しています...
            </div>
            <div className='text-[13px] text-(--bp-ink3)'>
              ヒアリングメモを整理中です。しばらくお待ちください。
            </div>
          </div>
        </PageContainer>
      </AppLayout>
    )
  }

  // ============================================================
  // 状態3+4: 2カラム マスター・ディテール
  // ============================================================
  function renderStatusBadge (note: SourceNote): React.ReactElement {
    if (note.pipeline_status === 'running' || note.pipeline_status === 'queued') {
      return <StatusBadge variant='running' label='処理中' />
    }
    if (note.status === 'structurizing') return <StatusBadge variant='running' label='レビュー待ち' />
    if (note.status === 'structured') return <StatusBadge variant='succeeded' label='構造化済み' />
    return <StatusBadge variant='editing' label='未処理' />
  }

  return (
    <AppLayout project={project} mainClassName='overflow-hidden flex flex-col'>
      {guideDialog}
      <FlashBanner />
      {(error?.errors?.guest_limit != null && error.errors.guest_limit !== '') && (
        <div className='px-5 pt-3'>
          <ErrorBanner message={error.errors.guest_limit} />
        </div>
      )}
      {pollTimedOut && (
        <div className='px-5 pt-3'>
          <ErrorBanner message='処理に時間がかかっています。ページを再読み込みしてください。' />
        </div>
      )}

      <div
        className='flex-1 overflow-hidden'
        style={{ display: 'grid', gridTemplateColumns: '280px 1fr' }}
      >
        {/* ===== 左ペイン: ノート一覧 ===== */}
        <div className='flex flex-col overflow-hidden bg-(--bp-surface) border-r border-(--bp-border)'>
          <div className='px-4 py-3 border-b border-(--bp-border) flex items-center justify-between shrink-0'>
            <div>
              <div className='text-[13px] font-bold text-(--bp-ink)'>ヒアリングノート</div>
              <div className='text-[11px] text-(--bp-ink3) mt-px'>{sortedNotes.length}件</div>
            </div>
            <div className='flex items-center gap-1.5'>
              <Button type='button' variant='ghost' className='py-[5px] px-2 text-[12px]' onClick={() => { guide.openHelp() }}>
                <CircleHelpIcon size={12} />
              </Button>
              <Button
                type='button'
                className='py-[5px] px-[11px] text-[12px]'
                onClick={() => { setModalOpen(true); setModalPhase('form') }}
              >
                <PlusIcon size={12} /> 追加
              </Button>
            </div>
          </div>

          <div className='flex-1 overflow-y-auto p-2 flex flex-col gap-1.5'>
            {sortedNotes.map((note) => {
              const isSelected = note.id === selectedNoteId
              const round = noteRound(note)
              return (
                <button
                  key={note.id}
                  type='button'
                  onClick={() => handleSelectNote(note.id)}
                  className={cn(
                    'w-full text-left px-3 py-2.5 rounded-lg border transition-all',
                    isSelected
                      ? 'bg-(--bp-accent-pale) border-[rgba(53,71,193,.35)]'
                      : 'bg-(--bp-surface) border-(--bp-border) hover:bg-(--bp-surface2) hover:border-(--bp-border-s)'
                  )}
                >
                  <div className='flex items-center gap-1.5 mb-1'>
                    <div
                      className='w-5 h-5 rounded-[4px] flex items-center justify-center text-[10px] font-bold shrink-0'
                      style={
                        note.kind === 'initial'
                          ? { background: 'var(--bp-ok-pale)', border: '1px solid var(--bp-ok-b)', color: 'var(--bp-ok)' }
                          : { background: 'var(--bp-surface2)', border: '1px solid var(--bp-border)', color: 'var(--bp-ink3)' }
                      }
                    >
                      {note.kind === 'initial' ? '初' : round}
                    </div>
                    <span
                      className={cn(
                        'text-[12px] font-semibold flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap',
                        isSelected ? 'text-(--bp-accent)' : 'text-(--bp-ink)'
                      )}
                    >
                      第{round}回ヒアリングノート
                    </span>
                  </div>
                  <div
                    className='text-[11px] text-(--bp-ink2) leading-[1.5] mb-1.5 overflow-hidden'
                    style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
                  >
                    {note.content}
                  </div>
                  <div className='flex items-center justify-between'>
                    {renderStatusBadge(note)}
                    <span className='text-[10px] text-(--bp-ink3)'>{formatDate(note.created_at)}</span>
                  </div>
                </button>
              )
            })}
          </div>
        </div>

        {/* ===== 右ペイン: ノート詳細 ===== */}
        <div className='flex flex-col overflow-hidden'>
          {(selectedNote == null)
            ? (
              <div className='flex-1 flex items-center justify-center text-[13px] text-(--bp-ink3)'>
                ノートを選択してください
              </div>
              )
            : (selectedNote.pipeline_status === 'running' || selectedNote.pipeline_status === 'queued')
                ? (
                  <div className='flex-1 flex flex-col items-center justify-center gap-3'>
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        border: '3px solid var(--bp-border)',
                        borderTopColor: 'var(--bp-accent)',
                        borderRadius: '50%',
                        animation: 'spin .8s linear infinite'
                      }}
                    />
                    <div className='text-[13px] text-(--bp-ink3)'>構造化処理中...</div>
                  </div>
                  )
                : (selectedNote.status === 'raw' && selectedNote.pipeline_status === 'failed')
                    ? (
                      <div className='flex-1 flex flex-col items-center justify-center gap-4 px-8'>
                        <div className='text-[13px] font-semibold text-(--bp-ink)'>構造化に失敗しました</div>
                        <div className='text-[12px] text-(--bp-ink3) text-center leading-[1.7]'>
                          同じ内容で再度構造化を実行できます。
                        </div>
                        <Button
                          type='button'
                          disabled={analyzingNoteId !== null}
                          onClick={() => {
                            if (analyzingNoteId !== null) return
                            setAnalyzingNoteId(selectedNote.id)
                            router.post(
                              `${rdBase}/source_notes/${selectedNote.id}/retry_structurize`,
                              {},
                              { onFinish: () => setAnalyzingNoteId(null) }
                            )
                          }}
                        >
                          {analyzingNoteId === selectedNote.id ? '送信中...' : '再試行する'}
                        </Button>
                      </div>
                      )
                    : (
                      <>
                        {/* 詳細ヘッダー */}
                        <div className='px-5 py-3 bg-(--bp-surface) border-b border-(--bp-border) flex items-center gap-3 shrink-0'>
                          <div className='flex-1 min-w-0'>
                            <div className='flex items-center gap-2 mb-0.5'>
                              <span className='text-[14px] font-bold text-(--bp-ink) tracking-tight'>
                                第{noteRound(selectedNote)}回ヒアリングノート
                              </span>
                              {renderStatusBadge(selectedNote)}
                            </div>
                            <div className='text-[11px] text-(--bp-ink3)'>{formatDate(selectedNote.created_at)}</div>
                          </div>
                          {selectedNote.status === 'structurizing' && (
                            <Button
                              type='button'
                              variant='outline'
                              className='py-1 px-2.5 text-[11px]'
                              onClick={() => {
                                if (!editMode && editContent === null) {
                                  setEditContent(selectedNote.structured_content ?? '')
                                }
                                setEditMode((v) => !v)
                              }}
                            >
                              {editMode ? <><EyeIcon size={11} /> プレビュー</> : <><PencilIcon size={11} /> 編集</>}
                            </Button>
                          )}
                        </div>

                        {/* タブバー */}
                        <div className='flex bg-(--bp-surface) border-b border-(--bp-border) px-5 shrink-0'>
                          {(['raw', 'structured'] as const).map((tab) => (
                            <button
                              key={tab}
                              type='button'
                              className={cn(
                                'h-[42px] px-4 text-[12px] font-medium border-b-2 transition-colors',
                                activeTab === tab
                                  ? 'text-(--bp-accent) border-(--bp-accent)'
                                  : 'text-(--bp-ink3) border-transparent hover:text-(--bp-ink)'
                              )}
                              onClick={() => setActiveTab(tab)}
                            >
                              {tab === 'raw' ? '原文' : '構造化メモ'}
                            </button>
                          ))}
                        </div>

                        {/* コンテンツ */}
                        <div className='flex-1 overflow-hidden relative'>
                          {activeTab === 'raw'
                            ? (
                              <div className='absolute inset-0 overflow-y-auto px-5 py-4'>
                                <pre
                                  style={{
                                    fontFamily: 'var(--bp-font-mono)',
                                    fontSize: 12,
                                    color: 'var(--bp-ink2)',
                                    whiteSpace: 'pre-wrap',
                                    lineHeight: 1.9,
                                    margin: 0
                                  }}
                                >
                                  {selectedNote.content}
                                </pre>
                              </div>
                              )
                            : editMode && selectedNote.status === 'structurizing'
                              ? (
                                <textarea
                                  style={{
                                    position: 'absolute',
                                    inset: 0,
                                    width: '100%',
                                    height: '100%',
                                    padding: '18px 20px',
                                    border: 'none',
                                    outline: 'none',
                                    resize: 'none',
                                    fontSize: 13,
                                    fontFamily: 'var(--bp-font-mono)',
                                    lineHeight: 1.85,
                                    background: 'var(--bp-surface)',
                                    color: 'var(--bp-ink)',
                                    boxSizing: 'border-box'
                                  }}
                                  value={editContent ?? ''}
                                  onChange={(e) => setEditContent(e.target.value)}
                                />
                                )
                              : (
                                <div className='absolute inset-0 overflow-y-auto px-5 py-4 bg-(--bp-surface)'>
                                  <div className='prose prose-sm'>
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                      {previewContent}
                                    </ReactMarkdown>
                                  </div>
                                </div>
                                )}
                        </div>

                        {/* フッター（structurizing のみ） */}
                        {selectedNote.status === 'structurizing' && (
                          <div className='px-5 py-3 bg-(--bp-surface) border-t border-(--bp-border) flex items-center justify-between shrink-0'>
                            <div className='text-[11px] text-(--bp-ink3)'>
                              構造化済みの内容を確認・修正したら解析を開始してください
                            </div>
                            <div className='flex gap-2'>
                              {isDirty && (
                                <Button
                                  type='button'
                                  variant='outline'
                                  onClick={() => { void handleSaveEdit(selectedNote) }}
                                  disabled={savingEdit}
                                >
                                  {savingEdit ? '保存中...' : '保存する'}
                                </Button>
                              )}
                              <Button
                                type='button'
                                onClick={() => { void handleAnalyze(selectedNote) }}
                                disabled={analyzingNoteId !== null}
                              >
                                {analyzingNoteId === selectedNote.id ? '解析中...' : '確定して解析を開始する →'}
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                      )}
        </div>
      </div>

      {/* ===== 追加ノート モーダル ===== */}
      {modalOpen && (
        <div
          className='fixed inset-0 z-50 flex items-center justify-center'
          style={{ background: 'rgba(0,0,0,.45)' }}
          onClick={(e) => {
            if (e.target === e.currentTarget && modalPhase === 'form') {
              setModalOpen(false)
              setModalContent('')
              setModalFile(null)
            }
          }}
        >
          <div
            className='bg-(--bp-surface) rounded-[12px] border border-(--bp-border) w-full max-w-[540px] mx-4 overflow-hidden'
            style={{ boxShadow: '0 20px 60px rgba(0,0,0,.18)' }}
          >
            <div className='px-6 py-4 border-b border-(--bp-border) flex items-center justify-between'>
              <div className='text-[15px] font-semibold text-(--bp-ink)'>ヒアリングノートを追加</div>
              {modalPhase === 'form' && (
                <Button
                  type='button'
                  variant='ghost'
                  className='py-1 px-2 text-[12px]'
                  onClick={() => { setModalOpen(false); setModalContent(''); setModalFile(null) }}
                >
                  ✕
                </Button>
              )}
            </div>

            {modalPhase === 'form'
              ? (
                <form onSubmit={handleModalSubmit}>
                  <div className='px-6 py-5'>
                    <div
                      style={
                      isDraggingModal
                        ? { borderColor: 'var(--bp-accent)', background: 'var(--bp-accent-pale)' }
                        : undefined
                    }
                    >
                      <textarea
                        className={fieldInputClass}
                        style={{ minHeight: 150, resize: 'vertical' }}
                        placeholder='追加のヒアリング内容を入力してください'
                        value={modalContent}
                        onChange={(e) => setModalContent(e.target.value)}
                        {...makeDragHandlers(setIsDraggingModal, setModalContent, setModalFile)}
                      />
                    </div>
                    <Button
                      type='button'
                      variant='ghost'
                      className='mt-1.5'
                      onClick={() => modalFileRef.current?.click()}
                    >
                      <PaperclipIcon size={13} /> ファイルから読み込む
                    </Button>
                    <input
                      ref={modalFileRef}
                      type='file'
                      style={{ display: 'none' }}
                      onChange={(e) => handleFileSelect(e, setModalContent, setModalFile)}
                    />
                  </div>
                  <div className='px-6 py-4 bg-(--bp-surface2) border-t border-(--bp-border) flex gap-2 justify-end'>
                    <Button
                      type='button'
                      variant='outline'
                      onClick={() => { setModalOpen(false); setModalContent(''); setModalFile(null) }}
                    >
                      キャンセル
                    </Button>
                    <Button type='submit' disabled={modalSubmitting}>
                      {modalSubmitting ? '送信中...' : '構造化を実行する'}
                    </Button>
                  </div>
                </form>
                )
              : (
                <div className='flex flex-col items-center justify-center py-12 px-6 gap-4'>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      border: '3px solid var(--bp-border)',
                      borderTopColor: 'var(--bp-accent)',
                      borderRadius: '50%',
                      animation: 'spin .8s linear infinite'
                    }}
                  />
                  <div className='text-[14px] font-semibold text-(--bp-ink)'>構造化処理中...</div>
                  <div className='text-[12px] text-(--bp-ink3) text-center leading-[1.7]'>
                    ヒアリングノートを整理しています。<br />完了後に自動で閉じます。
                  </div>
                </div>
                )}
          </div>
        </div>
      )}
    </AppLayout>
  )
}
